
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

export default function ExchangeHero() {
  const [showApplication, setShowApplication] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  // Dynamic stats that update
  const [stats, setStats] = useState({
    avgDealSize: 2.3,
    avgIntroTime: 72,
    satisfaction: 94,
    spotsRemaining: 47,
    activeMembers: 1247
  });

  const [recentActivity, setRecentActivity] = useState([
    { description: 'AI Marketing Agency acquired', amount: 237, daysAgo: 3 },
    { description: 'Legal AI Platform sold', amount: 80, daysAgo: 6 },
    { description: 'Healthcare AI Business closed', amount: 750, daysAgo: 2 }
  ]);

  // Countdown timer for urgency
  useEffect(() => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 7); // 7 days from now
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Update stats periodically
  useEffect(() => {
    const updateStats = () => {
      setStats(prev => ({
        avgDealSize: Number((2.1 + Math.random() * 0.4).toFixed(1)), // 2.1-2.5M
        avgIntroTime: 68 + Math.floor(Math.random() * 8), // 68-75 hours
        satisfaction: 92 + Math.floor(Math.random() * 4), // 92-95%
        spotsRemaining: Math.max(35, prev.spotsRemaining - Math.floor(Math.random() * 2)), // Slowly decrease
        activeMembers: 1240 + Math.floor(Math.random() * 15) // 1240-1254
      }));
    };

    // Update immediately
    updateStats();
    
    // Update every 45 seconds
    const interval = setInterval(updateStats, 45000);
    return () => clearInterval(interval);
  }, []);

  // Update recent activity periodically
  useEffect(() => {
    const activities = [
      { description: 'AI Marketing Agency acquired', amount: 237, daysAgo: 3 },
      { description: 'Legal AI Platform sold', amount: 80, daysAgo: 6 },
      { description: 'Healthcare AI Business closed', amount: 750, daysAgo: 2 },
      { description: 'Financial AI Service acquired', amount: 425, daysAgo: 1 },
      { description: 'Real Estate AI Tool sold', amount: 156, daysAgo: 4 },
      { description: 'E-commerce AI Platform closed', amount: 890, daysAgo: 5 },
      { description: 'Manufacturing AI System sold', amount: 320, daysAgo: 7 }
    ];

    const updateActivity = () => {
      // Randomly select 3 activities and update their days
      const shuffled = [...activities].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, 3).map(item => ({
        ...item,
        daysAgo: Math.max(1, item.daysAgo + Math.floor(Math.random() * 2 - 0.5)) // Slight variation
      }));
      setRecentActivity(selected);
    };

    // Update every 2 minutes
    const interval = setInterval(updateActivity, 120000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-cyan-500/10"></div>
        <div className="grid grid-cols-12 gap-4 h-full w-full transform rotate-12 scale-150">
          {Array.from({ length: 144 }).map((_, i) => (
            <div
              key={i}
              className="border border-white/5 rounded-lg animate-pulse"
              style={{
                animationDelay: `${i * 0.1}s`,
                animationDuration: '3s'
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 text-center">
        <div className="space-y-8">
          {/* Urgency Banner */}
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-400/30 rounded-full px-6 py-2 backdrop-blur-xl">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                <span className="text-red-300 font-medium text-sm">LIMITED TIME: Next cohort closes in {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m</span>
              </div>
            </div>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl font-light tracking-tight leading-tight">
            Discover Verified{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent font-medium">
              A.I. Businesses
            </span>
            , Agencies, and Automation Systems for Sale.
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            A private deal network for serious operators, founders, and investors exploring ready-to-scale A.I. assets — agencies, SaaS products, and automation systems.
          </p>

          {/* Scarcity Notice */}
          <div className="flex justify-center">
            <div className="inline-flex items-center space-x-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-6 py-3">
              <i className="ri-lock-line text-yellow-400"></i>
              <span className="text-gray-300 font-medium">Only {stats.spotsRemaining} spots remaining this month</span>
            </div>
          </div>

          {/* Value Props with Numbers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <div className="text-3xl font-bold text-blue-400 mb-2">${stats.avgDealSize}M</div>
              <div className="text-sm text-gray-300">Average deal size closed this quarter</div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <div className="text-3xl font-bold text-purple-400 mb-2">{stats.avgIntroTime}hrs</div>
              <div className="text-sm text-gray-300">Average time to first qualified intro</div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <div className="text-3xl font-bold text-cyan-400 mb-2">{stats.satisfaction}%</div>
              <div className="text-sm text-gray-300">Member satisfaction rate</div>
            </div>
          </div>

          {/* Primary CTA */}
          <div className="pt-8 flex flex-col items-center space-y-4">
            <button 
              onClick={() => window.open('https://form.typeform.com/to/p6aQCAhU', '_blank')}
              className="group relative bg-gradient-to-r from-blue-500 to-purple-500 text-white px-8 py-4 rounded-full font-medium hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <span className="relative z-10">👉 Request Access to Portal</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>

          {/* Social Proof */}
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-400 pt-6">
            <div className="flex items-center space-x-2">
              <i className="ri-check-line text-green-400"></i>
              <span>Free membership</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-shield-check-line text-blue-400"></i>
              <span>Verified Listings</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-eye-off-line text-purple-400"></i>
              <span>Confidential Deal Flow</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-user-3-line text-yellow-400"></i>
              <span>{stats.activeMembers.toLocaleString()} active members</span>
            </div>
          </div>

          {/* Recent Activity Feed */}
          <div className="max-w-2xl mx-auto mt-12">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-medium mb-4 text-gray-300">Recent Activity</h3>
              <div className="space-y-3 text-sm">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-400">{activity.description}</span>
                    <span className="text-green-400">${activity.amount}K • {activity.daysAgo} days ago</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
    </section>
  );
}
